import React from "react";
import {Box, IconButton, MenuItem, Popover, Select, TextField, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import {CalendarMonth, ChevronLeft, ChevronRight} from "@mui/icons-material";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {DateCalendar} from "@mui/x-date-pickers/DateCalendar";
import moment, {Moment} from "moment-timezone";
import CCButton from "./CCButton";
import css_buttons from "./css/CCButton.module.scss";
import {Black, Blue3, Grey3, Grey6} from "../../theme/styles";


// Unit codes match moment.js shorthands so serializeRange can pass them
// straight to moment.subtract. Note: "m" is minutes (lowercase), "M" is
// months (uppercase) — that's a moment convention, not a typo.
export type RelativeUnit = "m" | "h" | "d" | "w" | "M";

export type PlatformDateTimeRangeValue =
    | {mode: "absolute"; from: string | null; to: string | null}
    | {mode: "relative"; duration: number; unit: RelativeUnit}
    | null;


type Props = {
    value: PlatformDateTimeRangeValue;
    onChange: (v: PlatformDateTimeRangeValue) => void;
    label?: string;
    placeholder?: string;
    fullWidth?: boolean;
    size?: "small" | "medium";
    disabled?: boolean;
    timezone?: string;
};

type AbsoluteDraft = {
    startDate: Moment | null;
    endDate: Moment | null;
    startTime: string;
    endTime: string;
    leftMonth: Moment;
    rightMonth: Moment;
};

type RelativeDraft = {
    duration: number | "";
    unit: RelativeUnit;
};

type State = {
    anchorEl: HTMLElement | null;
    mode: "absolute" | "relative";
    absolute: AbsoluteDraft;
    relative: RelativeDraft;
    timezone: "local" | "utc";
    appliedRelative: {duration: number; unit: RelativeUnit} | null;
};

const QUICK_PRESETS: {label: string; duration: number; unit: RelativeUnit}[] = [
    {label: "1m", duration: 1, unit: "m"},
    {label: "30m", duration: 30, unit: "m"},
    {label: "1h", duration: 1, unit: "h"},
    {label: "12h", duration: 12, unit: "h"},
];

const UNIT_LABEL: Record<RelativeUnit, string> = {m: "Minutes", h: "Hours", d: "Days", w: "Weeks", M: "Months"};
// "mo" instead of "M" in the short display so the eye doesn't confuse
// "Last 6M" (months) with the minute abbreviation.
const UNIT_SHORT: Record<RelativeUnit, string> = {m: "m", h: "h", d: "d", w: "w", M: "mo"};

const RELATIVE_PRESETS: {unit: RelativeUnit; values: number[]}[] = [
    {unit: "m", values: [5, 10, 15, 30, 45]},
    {unit: "h", values: [1, 2, 3, 6, 8, 12]},
    {unit: "d", values: [1, 2, 3, 4, 5, 6]},
    {unit: "w", values: [1, 2, 3, 4]},
    {unit: "M", values: [1, 2, 3, 6, 9, 12]},
];

// Light theme palette
const SURFACE = "#FFFFFF";
const SURFACE_ALT = "#F7F9FC";
const BORDER = Grey3;          // "#D8DDE4"
const TEXT = Black;            // "#000000"
const MUTED = Grey6;           // "#777777"
const ACCENT = Blue3;          // "#1498D8"
const ACCENT_SOFT = "rgba(20, 152, 216, 0.12)";


function defaultAbsolute(now: Moment): AbsoluteDraft {
    let right = now.clone().startOf("month");
    let left = right.clone().subtract(1, "month");
    return {startDate: null, endDate: null, startTime: "", endTime: "", leftMonth: left, rightMonth: right};
}

function defaultRelative(): RelativeDraft {
    return {duration: "", unit: "m"};
}

function isInRange(day: Moment, start: Moment | null, end: Moment | null): "start" | "end" | "between" | null {
    if (!start) return null;
    let s = start.clone().startOf("day");
    let e = end ? end.clone().startOf("day") : null;
    let d = day.clone().startOf("day");
    if (e && d.isSame(s) && d.isSame(e)) return "start";
    if (d.isSame(s)) return "start";
    if (e && d.isSame(e)) return "end";
    if (e && d.isAfter(s) && d.isBefore(e)) return "between";
    return null;
}



class PlatformDateTimeRangeFilter extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        let initialMode: "absolute" | "relative" = props.value?.mode === "absolute" ? "absolute" : "relative";
        let initialApplied: {duration: number; unit: RelativeUnit} | null = null;
        if (props.value?.mode === "relative") {
            initialApplied = {duration: props.value.duration, unit: props.value.unit};
        }
        this.state = {
            anchorEl: null,
            mode: initialMode,
            absolute: defaultAbsolute(moment()),
            relative: defaultRelative(),
            timezone: "utc",
            appliedRelative: initialApplied,
        };
    }

    open = (anchor: HTMLElement) => {
        let v = this.props.value;
        let nextState: Partial<State> = {anchorEl: anchor};
        if (v?.mode === "relative") {
            nextState.relative = {duration: v.duration, unit: v.unit};
            nextState.absolute = defaultAbsolute(moment());
        } else if (v?.mode === "absolute") {
            let startDate = v.from ? moment(v.from) : null;
            let endDate = v.to ? moment(v.to) : null;
            let right = (endDate ?? startDate ?? moment()).clone().startOf("month");
            let left = right.clone().subtract(1, "month");
            nextState.absolute = {
                startDate,
                endDate,
                startTime: startDate ? startDate.format("HH:mm:ss") : "",
                endTime: endDate ? endDate.format("HH:mm:ss") : "",
                leftMonth: left,
                rightMonth: right,
            };
            nextState.relative = defaultRelative();
        } else {
            nextState.absolute = defaultAbsolute(moment());
            nextState.relative = defaultRelative();
        }
        // Preserve current mode — don't override it just because value changed.
        this.setState(nextState as State);
    };

    close = () => this.setState({anchorEl: null});

    setMode = (mode: "absolute" | "relative") => this.setState({mode});

    onAbsoluteDayClick = (day: Moment | null) => {
        if (!day) return;
        this.setState(s => {
            let a = s.absolute;
            if (!a.startDate || (a.startDate && a.endDate)) {
                return {absolute: {...a, startDate: day.clone(), endDate: null, startTime: a.startTime || "00:00:00", endTime: ""}} as State;
            }
            if (day.isBefore(a.startDate, "day")) {
                return {absolute: {...a, startDate: day.clone(), endDate: a.startDate, endTime: a.startTime, startTime: "00:00:00"}} as State;
            }
            return {absolute: {...a, endDate: day.clone(), endTime: a.endTime || "23:59:59"}} as State;
        });
    };

    setStartDateText = (v: string) => {
        let parsed = moment(v, "YYYY/MM/DD", true);
        this.setState(s => ({absolute: {...s.absolute, startDate: parsed.isValid() ? parsed : null}}));
    };
    setEndDateText = (v: string) => {
        let parsed = moment(v, "YYYY/MM/DD", true);
        this.setState(s => ({absolute: {...s.absolute, endDate: parsed.isValid() ? parsed : null}}));
    };
    setStartTimeText = (v: string) => this.setState(s => ({absolute: {...s.absolute, startTime: v}}));
    setEndTimeText = (v: string) => this.setState(s => ({absolute: {...s.absolute, endTime: v}}));

    setLeftMonth = (delta: number) => {
        this.setState(s => ({absolute: {...s.absolute, leftMonth: s.absolute.leftMonth.clone().add(delta, "month")}}));
    };
    setRightMonth = (delta: number) => {
        this.setState(s => ({absolute: {...s.absolute, rightMonth: s.absolute.rightMonth.clone().add(delta, "month")}}));
    };

    pickRelativePreset = (unit: RelativeUnit, value: number) => {
        this.setState({mode: "relative", relative: {duration: value, unit}}, () => this.applyRelative());
    };

    applyAbsolute = () => {
        let a = this.state.absolute;
        let from: string | null = null;
        let to: string | null = null;
        if (a.startDate) {
            let st = a.startTime && /^\d{2}:\d{2}(:\d{2})?$/.test(a.startTime) ? a.startTime : "00:00:00";
            let s = a.startDate.clone();
            let [hh, mm, ss] = st.split(":").map(n => parseInt(n, 10));
            s.set({hour: hh || 0, minute: mm || 0, second: ss || 0, millisecond: 0});
            from = s.toISOString();
        }
        if (a.endDate) {
            let et = a.endTime && /^\d{2}:\d{2}(:\d{2})?$/.test(a.endTime) ? a.endTime : "23:59:59";
            let e = a.endDate.clone();
            let [hh, mm, ss] = et.split(":").map(n => parseInt(n, 10));
            e.set({hour: hh || 0, minute: mm || 0, second: ss || 0, millisecond: 0});
            to = e.toISOString();
        }
        this.setState({appliedRelative: null});
        this.props.onChange({mode: "absolute", from, to});
        this.close();
    };

    applyRelative = () => {
        let r = this.state.relative;
        let duration = typeof r.duration === "number" ? r.duration : parseInt(String(r.duration || "0"), 10);
        if (!duration || duration <= 0) {
            this.setState({appliedRelative: null});
            this.props.onChange(null);
        } else {
            this.setState({appliedRelative: {duration, unit: r.unit}});
            this.props.onChange({mode: "relative", duration, unit: r.unit});
        }
        this.close();
    };

    clearAll = () => {
        this.setState({appliedRelative: null});
        this.props.onChange(null);
        this.close();
    };

    apply = () => {
        if (this.state.mode === "relative") this.applyRelative();
        else this.applyAbsolute();
    };

    quickApply = (duration: number, unit: RelativeUnit) => {
        this.setState({appliedRelative: {duration, unit}, mode: "relative"});
        this.props.onChange({mode: "relative", duration, unit});
    };

    onTimezoneChange = (tz: "local" | "utc") => {
        this.setState({timezone: tz});
    };

    triggerRef: HTMLElement | null = null;

    openCustom = () => {
        if (this.triggerRef) this.open(this.triggerRef);
    };

    renderTimezoneSelect(disabled: boolean) {
        return (
            <div style={{borderLeft: `1px solid ${BORDER}`}}>
                <Select
                    size="small"
                    value={this.state.timezone}
                    disabled={disabled}
                    onChange={(e) => this.onTimezoneChange(e.target.value as "local" | "utc")}
                    variant="standard"
                    disableUnderline
                    sx={{
                        fontSize: 12,
                        padding: "0 6px",
                        "& .MuiSelect-select": {paddingTop: "4px", paddingBottom: "4px"},
                    }}
                    renderValue={(v) => v === "utc" ? "UTC" : "Local"}
                >
                    <MenuItem value="local">Local timezone</MenuItem>
                    <MenuItem value="utc">UTC timezone</MenuItem>
                </Select>
            </div>
        );
    }

    renderRelativeToolbar(disabled: boolean) {
        let ar = this.state.appliedRelative;
        let activeKey: string | null = ar ? `${ar.duration}${ar.unit}` : null;
        let hasValue = !!this.props.value || !!ar;
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", alignItems: "center", flex: "1 1 auto"}}>
                    <ToolbarLink onClick={this.clearAll} disabled={disabled || !hasValue}>Clear</ToolbarLink>
                    {QUICK_PRESETS.map(p => {
                        let key = `${p.duration}${p.unit}`;
                        let active = key === activeKey;
                        return (
                            <ToolbarLink
                                key={p.label}
                                onClick={() => this.quickApply(p.duration, p.unit)}
                                disabled={disabled}
                                accent={active}
                            >
                                {p.label}
                            </ToolbarLink>
                        );
                    })}
                    <ToolbarLink onClick={this.openCustom} disabled={disabled} accent>
                        Custom
                        {ar ? (
                            <span style={{marginLeft: 4, color: TEXT}}>(Last {ar.duration}{UNIT_SHORT[ar.unit]})</span>
                        ) : null}
                        <CalendarMonth fontSize="small" style={{marginLeft: 4, verticalAlign: "middle", width: 16, height: 16}}/>
                    </ToolbarLink>
                </div>
                {this.renderTimezoneSelect(disabled)}
            </>
        );
    }

    renderAbsoluteToolbar(disabled: boolean) {
        let v = this.props.value;
        let fromText = v?.mode === "absolute" && v.from ? this.formatInTz(moment(v.from), "YYYY/MM/DD HH:mm") : "";
        let toText = v?.mode === "absolute" && v.to ? this.formatInTz(moment(v.to), "YYYY/MM/DD HH:mm") : "";
        return (
            <>
                <div
                    onClick={disabled ? undefined : this.openCustom}
                    style={{
                        flex: "1 1 auto",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        padding: "3px 8px",
                        fontSize: 12,
                        cursor: disabled ? "default" : "pointer",
                        gap: 6,
                    }}
                >
                    <span style={{color: fromText ? TEXT : MUTED, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {fromText || "Start date"}
                    </span>
                    <span style={{color: MUTED}}>{">"}</span>
                    <span style={{color: toText ? TEXT : MUTED, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {toText || "End date"}
                    </span>
                    <IconButton size="small" disabled={disabled} onClick={(e) => {e.stopPropagation(); this.openCustom();}} style={{padding: 2}}>
                        <CalendarMonth fontSize="small" style={{color: ACCENT, width: 16, height: 16}}/>
                    </IconButton>
                </div>
                {this.renderTimezoneSelect(disabled)}
            </>
        );
    }

    formatInTz(m: Moment, fmt: string): string {
        return this.state.timezone === "utc" ? m.clone().utc().format(fmt) : m.clone().local().format(fmt);
    }

    renderTrigger() {
        let disabled = !!this.props.disabled;
        let label = this.props.label;
        return (
            <fieldset
                ref={(el) => {this.triggerRef = el;}}
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 4,
                    backgroundColor: disabled ? SURFACE_ALT : SURFACE,
                    width: this.props.fullWidth !== false ? "100%" : undefined,
                    height: 50,
                    boxSizing: "border-box",
                    overflow: "hidden",
                    opacity: disabled ? 0.6 : 1,
                    margin: 0,
                    padding: "0 4px",
                    minWidth: 0,
                }}
            >
                {label ? (
                    <legend style={{padding: "0 4px", fontSize: 11, color: MUTED, marginLeft: 6, lineHeight: 1}}>
                        {label}
                    </legend>
                ) : null}
                {this.state.mode === "relative" ? this.renderRelativeToolbar(disabled) : this.renderAbsoluteToolbar(disabled)}
            </fieldset>
        );
    }

    renderDayCell(month: Moment, day: number, start: Moment | null, end: Moment | null) {
        let date = month.clone().date(day);
        let kind = isInRange(date, start, end);
        let isEndpoint = kind === "start" || kind === "end";
        let bg = isEndpoint ? ACCENT : kind === "between" ? ACCENT_SOFT : "transparent";
        let color = isEndpoint ? "#FFFFFF" : TEXT;
        let border = "1px solid transparent";
        return (
            <div
                key={`${month.format("YYYY-MM")}-${day}`}
                onClick={() => this.onAbsoluteDayClick(date)}
                style={{
                    width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", color, fontSize: 13, backgroundColor: bg, border, borderRadius: 4,
                }}
            >
                {day}
            </div>
        );
    }

    renderMonth(month: Moment, onNav: (delta: number) => void) {
        let start = this.state.absolute.startDate;
        let end = this.state.absolute.endDate;
        let firstWeekday = month.clone().startOf("month").day();
        let daysInMonth = month.daysInMonth();
        let cells: React.ReactNode[] = [];
        for (let i = 0; i < firstWeekday; i++) cells.push(<div key={`pad-${i}`} style={{width: 36, height: 36}}/>);
        for (let d = 1; d <= daysInMonth; d++) cells.push(this.renderDayCell(month, d, start, end));
        let weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return (
            <div style={{flex: 1, padding: "0 8px"}}>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6}}>
                    <IconButton size="small" onClick={() => onNav(-1)} style={{color: MUTED}}><ChevronLeft fontSize="small"/></IconButton>
                    <Typography style={{fontSize: 14, fontWeight: 500, color: TEXT}}>{month.format("MMMM YYYY")}</Typography>
                    <IconButton size="small" onClick={() => onNav(1)} style={{color: MUTED}}><ChevronRight fontSize="small"/></IconButton>
                </div>
                <div style={{display: "grid", gridTemplateColumns: "repeat(7, 36px)", gap: 0}}>
                    {weekdays.map(w => <div key={w} style={{textAlign: "center", fontSize: 11, color: MUTED, padding: "4px 0"}}>{w}</div>)}
                    {cells}
                </div>
            </div>
        );
    }

    renderTabs() {
        return (
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", padding: "12px 16px 8px 16px"}}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={this.state.mode}
                    onChange={(_, v) => v && this.setMode(v)}
                    sx={{
                        borderRadius: "9999px",
                        backgroundColor: SURFACE_ALT,
                        padding: "2px",
                        border: `1px solid ${BORDER}`,
                        "& .MuiToggleButton-root": {
                            color: MUTED, border: "none", borderRadius: "9999px !important", padding: "4px 18px",
                            textTransform: "none", fontSize: 13,
                        },
                        "& .MuiToggleButton-root.Mui-selected": {
                            backgroundColor: ACCENT, color: "#FFFFFF",
                            "&:hover": {backgroundColor: ACCENT},
                        },
                    }}
                >
                    <ToggleButton value="absolute">Absolute</ToggleButton>
                    <ToggleButton value="relative">Relative</ToggleButton>
                </ToggleButtonGroup>
            </div>
        );
    }

    renderAbsolute() {
        let a = this.state.absolute;
        return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <div style={{padding: "0 8px"}}>
                    <div style={{display: "flex", flexDirection: "row"}}>
                        {this.renderMonth(a.leftMonth, this.setLeftMonth)}
                        {this.renderMonth(a.rightMonth, this.setRightMonth)}
                    </div>
                    <div style={{display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, padding: "12px 16px 4px 16px"}}>
                        <div>
                            <Typography style={{fontSize: 12, color: MUTED, marginBottom: 4}}>Start date</Typography>
                            <TextField fullWidth size="small" placeholder="YYYY/MM/DD" value={a.startDate ? a.startDate.format("YYYY/MM/DD") : ""} onChange={(e) => this.setStartDateText(e.target.value)}/>
                        </div>
                        <div>
                            <Typography style={{fontSize: 12, color: MUTED, marginBottom: 4}}>Start time</Typography>
                            <TextField fullWidth size="small" placeholder="hh:mm:ss" value={a.startTime} onChange={(e) => this.setStartTimeText(e.target.value)}/>
                        </div>
                        <div>
                            <Typography style={{fontSize: 12, color: MUTED, marginBottom: 4}}>End date</Typography>
                            <TextField fullWidth size="small" placeholder="YYYY/MM/DD" value={a.endDate ? a.endDate.format("YYYY/MM/DD") : ""} onChange={(e) => this.setEndDateText(e.target.value)}/>
                        </div>
                        <div>
                            <Typography style={{fontSize: 12, color: MUTED, marginBottom: 4}}>End time</Typography>
                            <TextField fullWidth size="small" placeholder="hh:mm:ss" value={a.endTime} onChange={(e) => this.setEndTimeText(e.target.value)}/>
                        </div>
                    </div>
                    <Typography style={{fontSize: 11, color: MUTED, padding: "4px 16px 8px 16px"}}>For date, use YYYY/MM/DD. For time, use hh:mm:ss 24 hr format.</Typography>
                </div>
                {/* keep DateCalendar import referenced to avoid unused-warning if MUI changes; not rendered */}
                <div style={{display: "none"}}><DateCalendar value={null} onChange={() => {}}/></div>
            </LocalizationProvider>
        );
    }

    renderRelative() {
        let r = this.state.relative;
        return (
            <div style={{padding: "0 16px 8px 16px"}}>
                {RELATIVE_PRESETS.map(row => (
                    <div key={row.unit} style={{display: "flex", flexDirection: "row", alignItems: "center", padding: "6px 0"}}>
                        <Typography style={{fontSize: 12, color: MUTED, width: 70}}>{UNIT_LABEL[row.unit]}</Typography>
                        <div style={{display: "flex", flexDirection: "row", gap: 6, flexWrap: "wrap"}}>
                            {row.values.map(v => (
                                <div
                                    key={`${row.unit}-${v}`}
                                    onClick={() => this.pickRelativePreset(row.unit, v)}
                                    style={{
                                        minWidth: 44, padding: "5px 8px", border: `1px solid ${BORDER}`, borderRadius: 4,
                                        textAlign: "center", fontSize: 13, color: TEXT, cursor: "pointer", backgroundColor: SURFACE,
                                    }}
                                >
                                    {v}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                <div style={{display: "flex", flexDirection: "row", gap: 10, marginTop: 10}}>
                    <div style={{flex: "0 0 110px"}}>
                        <Typography style={{fontSize: 12, color: MUTED, marginBottom: 4}}>Duration</Typography>
                        <TextField
                            fullWidth size="small" type="number"
                            value={r.duration}
                            onChange={(e) => {
                                let raw = e.target.value;
                                if (raw === "") this.setState({relative: {...r, duration: ""}});
                                else {
                                    let n = Math.min(9999, Math.max(0, parseInt(raw, 10) || 0));
                                    this.setState({relative: {...r, duration: n}});
                                }
                            }}
                        />
                        <Typography style={{fontSize: 11, color: MUTED, marginTop: 4}}>Up to 4 digits.</Typography>
                    </div>
                    <div style={{flex: 1}}>
                        <Typography style={{fontSize: 12, color: MUTED, marginBottom: 4}}>Unit of time</Typography>
                        <Select
                            fullWidth size="small"
                            value={r.unit}
                            onChange={(e) => this.setState({relative: {...r, unit: e.target.value as RelativeUnit}})}
                        >
                            {(Object.keys(UNIT_LABEL) as RelativeUnit[]).map(u => (
                                <MenuItem key={u} value={u}>{UNIT_LABEL[u]}</MenuItem>
                            ))}
                        </Select>
                    </div>
                </div>
            </div>
        );
    }

    renderFooter() {
        return (
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: `1px solid ${BORDER}`, backgroundColor: SURFACE_ALT}}>
                <CCButton className={css_buttons.cancelStandardButton} onClick={this.clearAll}>Clear</CCButton>
                <div style={{display: "flex", flexDirection: "row", gap: 8}}>
                    <CCButton className={css_buttons.cancelStandardButton} onClick={this.close}>Cancel</CCButton>
                    <CCButton className={css_buttons.blueStandardButton} onClick={this.apply}>Apply</CCButton>
                </div>
            </div>
        );
    }

    render() {
        return (
            <>
                {this.renderTrigger()}
                <Popover
                    open={!!this.state.anchorEl}
                    anchorEl={this.state.anchorEl}
                    onClose={this.close}
                    anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                    slotProps={{paper: {sx: {backgroundColor: SURFACE, color: TEXT, border: `1px solid ${BORDER}`, width: 600}}}}
                >
                    <Box>
                        {this.renderTabs()}
                        {this.state.mode === "absolute" ? this.renderAbsolute() : this.renderRelative()}
                        {this.renderFooter()}
                    </Box>
                </Popover>
            </>
        );
    }
}


function ToolbarLink({onClick, disabled, accent, children}: {onClick: () => void; disabled?: boolean; accent?: boolean; children: React.ReactNode}) {
    let color = disabled ? Grey3 : accent ? ACCENT : MUTED;
    return (
        <div
            onClick={disabled ? undefined : onClick}
            style={{
                padding: "3px 6px",
                fontSize: 12,
                color,
                cursor: disabled ? "default" : "pointer",
                userSelect: "none",
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </div>
    );
}


export default PlatformDateTimeRangeFilter;

export function serializeRange(v: PlatformDateTimeRangeValue): string {
    if (!v) return "";
    if (v.mode === "relative") {
        let now = moment();
        let from = now.clone().subtract(v.duration, v.unit as moment.unitOfTime.DurationConstructor).toISOString();
        let to = now.toISOString();
        return `${from}|${to}`;
    }
    return `${v.from ?? ""}|${v.to ?? ""}`;
}

export function deserializeRange(s: string | null | undefined): PlatformDateTimeRangeValue {
    if (!s) return null;
    if (typeof s !== "string") return null;
    let parts = s.split("|");
    if (parts.length !== 2) return null;
    if (!parts[0] && !parts[1]) return null;
    return {mode: "absolute", from: parts[0] || null, to: parts[1] || null};
}
